import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/card';
import { MapPin, Car, RefreshCw, Users, Circle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const LiveDriversMap = ({ 
  backendUrl,
  showTitle = true,
  height = "h-80",
  autoRefresh = true,
  refreshInterval = 15000
}) => {
  const [onlineDrivers, setOnlineDrivers] = useState([]);
  const [driversWithoutLocation, setDriversWithoutLocation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  // Fetch all online drivers
  const fetchOnlineDrivers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/admin/drivers/online-status`);
      const allDrivers = response.data.drivers || [];
      
      // Separate drivers with and without location
      const withLocation = allDrivers.filter(d => 
        d.current_location && d.current_location.lat && d.current_location.lng
      );
      const withoutLocation = allDrivers.filter(d => 
        !d.current_location || !d.current_location.lat || !d.current_location.lng
      );
      
      setOnlineDrivers(withLocation);
      setDriversWithoutLocation(withoutLocation.length);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching online drivers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOnlineDrivers();
    
    if (autoRefresh) {
      const interval = setInterval(fetchOnlineDrivers, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [backendUrl, autoRefresh, refreshInterval]);

  // Initialize and update map
  useEffect(() => {
    if (!mapRef.current) return;

    try {
      if (typeof window.L === 'undefined') {
        setMapError(true);
        return;
      }

      const L = window.L;
      
      // Initialize map if not already done
      if (!mapInstanceRef.current) {
        // Default center: Fort Lauderdale area
        const defaultCenter = [26.1224, -80.1373];
        mapInstanceRef.current = L.map(mapRef.current).setView(defaultCenter, 11);
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        }).addTo(mapInstanceRef.current);
      }

      // Clear existing markers
      markersRef.current.forEach(marker => {
        mapInstanceRef.current.removeLayer(marker);
      });
      markersRef.current = [];

      // Add driver markers with J-Style logo
      onlineDrivers.forEach(driver => {
        const lat = driver.current_location?.lat;
        const lng = driver.current_location?.lng;
        
        if (!lat || !lng) return;

        const carIcon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div style="
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 3px solid ${driver.online ? '#22c55e' : '#94a3b8'};
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            overflow: hidden;
            background: #1e3a5f;
          ">
            <img src="/jstyle-logo-marker.png" style="width: 100%; height: 100%; object-fit: cover;" alt="Driver" />
          </div>`,
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });

        const marker = L.marker([lat, lng], { icon: carIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="min-width: 180px;">
              <div style="font-weight: bold; font-size: 14px; margin-bottom: 5px;">${driver.name}</div>
              <div style="font-size: 12px; color: #666;">
                <p>🚗 ${driver.vehicle_type || 'Unknown'} - ${driver.vehicle_model || ''}</p>
                <p>📱 ${driver.phone || 'N/A'}</p>
                <p>⭐ Rating: ${driver.rating?.toFixed(1) || 'N/A'}</p>
                <p style="margin-top: 5px;">
                  <span style="
                    background: ${driver.online ? '#22c55e' : '#94a3b8'}; 
                    color: white; 
                    padding: 2px 8px; 
                    border-radius: 10px; 
                    font-size: 11px;
                  ">${driver.online ? '● Online' : '○ Offline'}</span>
                </p>
              </div>
            </div>
          `);
        
        markersRef.current.push(marker);
      });

      // Fit bounds if we have drivers
      if (onlineDrivers.length > 0) {
        const points = onlineDrivers
          .filter(d => d.current_location?.lat && d.current_location?.lng)
          .map(d => [d.current_location.lat, d.current_location.lng]);
        
        if (points.length > 0) {
          const bounds = L.latLngBounds(points);
          mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
        }
      }

    } catch (error) {
      console.error('Map error:', error);
      setMapError(true);
    }
  }, [onlineDrivers]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <Card className="p-4 border-2 border-green-200 bg-white">
      {showTitle && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <MapPin className="text-green-500" size={20} />
            Live Driver Map
          </h3>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchOnlineDrivers}
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      )}

      {/* Status Summary */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full">
          <Circle className="fill-green-500 text-green-500" size={10} />
          <span className="text-sm font-medium">{onlineDrivers.length} Online</span>
          <span className="text-xs text-gray-500">(on map)</span>
        </div>
        {driversWithoutLocation > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-full">
            <Circle className="fill-amber-500 text-amber-500" size={10} />
            <span className="text-sm font-medium">+{driversWithoutLocation} Online</span>
            <span className="text-xs text-gray-500">(no GPS)</span>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2">
              <RefreshCw className="animate-spin text-green-500" size={24} />
              <span className="text-sm text-gray-600">Updating...</span>
            </div>
          </div>
        )}
        
        {mapError ? (
          <div className={`${height} bg-gray-100 rounded-lg flex items-center justify-center`}>
            <div className="text-center text-gray-500">
              <MapPin size={32} className="mx-auto mb-2 opacity-50" />
              <p>Map unavailable</p>
            </div>
          </div>
        ) : (
          <div 
            ref={mapRef} 
            className={`${height} rounded-lg border border-gray-200`}
            style={{ zIndex: 1 }}
          />
        )}
      </div>

      {/* No drivers message */}
      {!isLoading && onlineDrivers.length === 0 && driversWithoutLocation === 0 && (
        <div className="mt-3 text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
          <Users size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No drivers currently online</p>
        </div>
      )}

      {/* Online Drivers List - Always visible for clarity */}
      {onlineDrivers.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Users size={16} className="text-green-500" />
            Online Drivers ({onlineDrivers.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {onlineDrivers.map((driver) => (
              <div 
                key={driver.id} 
                className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{driver.name}</p>
                  <p className="text-xs text-gray-500 truncate">{driver.vehicle_type || 'Unknown vehicle'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default LiveDriversMap;
